import { gsap } from 'gsap';

const getBoundingClientRectWithOffset = (element, container) => {
  const rect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return {
    top: rect.top - containerRect.top,
    left: rect.left - containerRect.left,
    width: rect.width,
    height: rect.height
  };
};

const createClone = (node, position, container, isToFront) => {
  const clone = node.cloneNode(true);

  // Set base styles that are common for both front-to-back and back-to-front animations
  const baseStyles = {
    top: position.top,
    width: position.width,
    height: position.height,
    zIndex: 9999
  };

  // Append the clone to the container first to ensure it's part of the DOM
  container.appendChild(clone);

  // Set the initial styles for the clone
  gsap.set(clone, { ...baseStyles });

  const backToFrontStyles = {
    position: 'absolute', // Keep absolute instead of fixed for mobile / tablet devices
    left: position.left,
    transform: 'translate(0, 0)'
  };

  const frontToBackStyles = {
    position: 'absolute',
    left: position.left,
    transform: undefined
  };

  // Apply the appropriate styles based on the direction of the flip
  const styles = isToFront ? { ...baseStyles, ...backToFrontStyles } : { ...baseStyles, ...frontToBackStyles };
  gsap.set(clone, styles);

  return clone;
};

const animateClone = (clone, toPosition, isToFront, onComplete) => {
  const rotationY = isToFront ? 180 : -180;
  const initialStyles = {
    transformOrigin: 'center center',
    transform: isToFront ? undefined : 'scale(1.05)'
  };

  gsap.set(clone, initialStyles);

  const tl = gsap.timeline({
    onUpdate: () => {
      const currentRotationY = gsap.getProperty(clone, 'rotationY');
      if (Math.abs(Math.round(Number(currentRotationY))) >= 90) {
        clone.innerHTML = '';
      }
    },
    onComplete: () => {
      if (onComplete) {
        onComplete();
      }
      clone.remove();
    }
  });

  tl.to(clone, {
    duration: 0.3,
    scale: isToFront ? 0.9 : 1.1,
    ease: 'power1.out'
  });

  tl.to(
    clone,
    {
      duration: 0.5,
      rotationY: rotationY,
      scale: 1,
      x: toPosition.left - clone.offsetLeft,
      y: toPosition.top - clone.offsetTop,
      width: toPosition.width,
      height: toPosition.height,
      ease: 'power1.out'
    },
    '+=0'
  ); // Start immediately after the previous animation

  return tl;
};

export const animateHero = ({
  cardFrontRef,
  cardBackRef,
  cardRef,
  animationContainerRef,
  flipped,
  onComplete = () => null
}) => {
  const containerNode = cardRef.current;
  const fromNode = flipped ? cardBackRef.current : cardFrontRef.current;
  const toNode = flipped ? cardFrontRef.current : cardBackRef.current;
  const animationContainer = animationContainerRef.current;

  const fromPosition = getBoundingClientRectWithOffset(fromNode, containerNode);
  const toPosition = getBoundingClientRectWithOffset(toNode, containerNode);

  const clone = createClone(fromNode, fromPosition, animationContainer, flipped);

  gsap.set([fromNode, toNode], { visibility: 'hidden' });

  animateClone(clone, toPosition, flipped, () => {
    gsap.set(toNode, { visibility: 'visible' });

    onComplete();
  });
};

export const flipWithoutAnimation = ({ cardFrontRef, cardBackRef, flipped, onComplete = () => null }) => {
  const fromNode = flipped ? cardBackRef.current : cardFrontRef.current;
  const toNode = flipped ? cardFrontRef.current : cardBackRef.current;

  gsap.set([fromNode], { visibility: 'hidden' });
  gsap.set(toNode, { visibility: 'visible' });

  onComplete();
};
